import { z } from 'zod';
import { Status } from '../identity-contracts';
import type {
    LogicalTerminalConfigurationStorageV2,
    LogicalTerminalConfiguration,
    StoredLogicalTerminalConfiguration,
    StoredTerminalDeviceConfiguration,
    TerminalDeviceConfigurationStorageV2,
    TerminalDeviceConfiguration,
    ProcessorSearchPageRequest,
    ProcessorSearchRequest,
    ProcessorListItem,
    ProcessorSearchPageResponse,
    CreateProcessorRequest,
    GetProcessorResponse,
    UpdateProcessorRequest,
    MerchantAccountListItem,
    MerchantAccountSearchListItem,
    MerchantAccountSearchPageRequest,
    MerchantAccountSearchRequest,
    MerchantAccountSearchPageResponse,
    CreateMerchantAccountRequest,
    GetMerchantAccountResponse,
    UpdateMerchantAccountRequest,
    LogicalTerminalSearchPageRequest,
    LogicalTerminalSearchRequest,
    LogicalTerminalSearchPageResponse,
    CreateLogicalTerminalRequest,
    UpdateLogicalTerminalRequest,
    GetLogicalTerminalResponse,
    TerminalDeviceSearchPageRequest,
    TerminalDeviceSearchRequest,
    CreateTerminalDeviceRequest,
    UpdateTerminalDeviceRequest,
    GetTerminalDeviceResponse,
    MerchantOrganizationSearchPageRequest,
    MerchantOrganizationSearchRequest,
    PreviewLogicalTerminalOrganizationChangeRequest,
} from '../payments_infrastructure.types';
import {
    LogicalTerminalConfigurationStorageVersion,
    MerchantAccountConfigurationSchema,
    normalizeStoredFeeConfiguration,
    normalizeStoredVersionedFeeConfiguration,
    TerminalDeviceConfigurationStorageVersion,
} from '../payments_infrastructure.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    NonEmptyStringSchema,
    PositiveIntSchema,
    SearchTermsSchema } from './common.schemas';
import { NumericIpgSystemIdSchema } from './third-party-system.schemas';
import { BusinessDetailsSchema, ProcessorConfigSchema } from '../backend_config.types';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';

export { MerchantAccountConfigurationSchema };

const SearchScopeOrganizationUuidSchema:
    z.ZodType<string | undefined> =
    z.string().uuid().optional();

export const PaymentsInfrastructureLimitSchema: z.ZodType<number | undefined> = z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .transform((limitValue: number | undefined) => {
        if (limitValue === undefined) {
            return undefined;
        }
        return Math.min(limitValue, 500);
    });

export const LogicalTerminalConfigurationSchema: z.ZodType<LogicalTerminalConfiguration> = z.object({
    allow_override_fee: z.boolean().optional(),
    surcharge_pct: z.number().min(0).max(100).optional(),
    convenience_fee: z.number().min(0).optional(),
});

export const TerminalDeviceConfigurationSchema: z.ZodType<TerminalDeviceConfiguration> = z.object({
    allow_override_fee: z.boolean().optional(),
    surcharge_pct: z.number().min(0).max(100).optional(),
    convenience_fee: z.number().min(0).optional(),
});

export const LogicalTerminalConfigurationStorageVersionSchema:
    z.ZodType<typeof LogicalTerminalConfigurationStorageVersion.V2> =
    z.literal(LogicalTerminalConfigurationStorageVersion.V2);

export const LogicalTerminalConfigurationStorageV1Schema:
    z.ZodType<LogicalTerminalConfiguration> =
    LogicalTerminalConfigurationSchema;

export const LogicalTerminalConfigurationStorageV2Schema:
    z.ZodType<LogicalTerminalConfigurationStorageV2> =
    z.object({
        logical_terminal_configuration_version:
            LogicalTerminalConfigurationStorageVersionSchema,
        config: LogicalTerminalConfigurationSchema,
    }).strict();

export const StoredLogicalTerminalConfigurationStorageV1Schema:
    z.ZodType<LogicalTerminalConfiguration> = z.preprocess(
        normalizeStoredFeeConfiguration,
        LogicalTerminalConfigurationStorageV1Schema
    );

export const StoredLogicalTerminalConfigurationStorageV2Schema:
    z.ZodType<LogicalTerminalConfigurationStorageV2> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredVersionedFeeConfiguration(
                value,
                'logical_terminal_configuration_version'
            ),
        LogicalTerminalConfigurationStorageV2Schema
    );

export const LogicalTerminalConfigurationVersionedObjectMarkerSchema:
    z.ZodType<{
        readonly logical_terminal_configuration_version: unknown;
    }> =
    z.object({
        logical_terminal_configuration_version: z.unknown(),
    }).passthrough();

export const StoredLogicalTerminalConfigurationSchema:
    z.ZodType<StoredLogicalTerminalConfiguration> =
    z.union([
        StoredLogicalTerminalConfigurationStorageV2Schema,
        StoredLogicalTerminalConfigurationStorageV1Schema,
    ]);

export function normalizeLogicalTerminalConfigurationStorageV1(
    config: LogicalTerminalConfiguration
): LogicalTerminalConfiguration {
    return config;
}

export function normalizeLogicalTerminalConfigurationStorageV2(
    config: LogicalTerminalConfigurationStorageV2
): LogicalTerminalConfiguration {
    return config.config;
}

export const LogicalTerminalConfigurationStorageContract =
    defineStoredJsonContract({
        contractName: 'LogicalTerminalConfiguration',
        semanticFieldValues: {},
        variants: {
            v1: {
                schemaName:
                    'StoredLogicalTerminalConfigurationStorageV1Schema',
                schema:
                    StoredLogicalTerminalConfigurationStorageV1Schema,
                guards: [
                    {
                        kind: 'json_marker_absent',
                        markerPath: [
                            'logical_terminal_configuration_version'],
                    },
                ],
                normalize:
                    normalizeLogicalTerminalConfigurationStorageV1,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
            v2: {
                schemaName:
                    'StoredLogicalTerminalConfigurationStorageV2Schema',
                schema:
                    StoredLogicalTerminalConfigurationStorageV2Schema,
                guards: [
                    {
                        kind: 'json_marker_equals',
                        markerPath: [
                            'logical_terminal_configuration_version'],
                        markerValue:
                            LogicalTerminalConfigurationStorageVersion.V2,
                    },
                ],
                normalize:
                    normalizeLogicalTerminalConfigurationStorageV2,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
        },
        writerVariant: 'v2',
    });

export const StoredLogicalTerminalConfigurationParser:
    z.ZodType<LogicalTerminalConfiguration> =
    buildStoredJsonValueParser(
        LogicalTerminalConfigurationStorageContract
    );

export function serializeLogicalTerminalConfigurationV2(
    config: LogicalTerminalConfiguration
): LogicalTerminalConfigurationStorageV2 {
    return {
        logical_terminal_configuration_version:
            LogicalTerminalConfigurationStorageVersion.V2,
        config: LogicalTerminalConfigurationSchema.parse(config),
    };
}

export const TerminalDeviceConfigurationStorageVersionSchema:
    z.ZodType<typeof TerminalDeviceConfigurationStorageVersion.V2> =
    z.literal(TerminalDeviceConfigurationStorageVersion.V2);

export const TerminalDeviceConfigurationStorageV1Schema:
    z.ZodType<TerminalDeviceConfiguration> =
    TerminalDeviceConfigurationSchema;

export const TerminalDeviceConfigurationStorageV2Schema:
    z.ZodType<TerminalDeviceConfigurationStorageV2> =
    z.object({
        terminal_device_configuration_version:
            TerminalDeviceConfigurationStorageVersionSchema,
        config: TerminalDeviceConfigurationSchema,
    }).strict();

export const StoredTerminalDeviceConfigurationStorageV1Schema:
    z.ZodType<TerminalDeviceConfiguration> = z.preprocess(
        normalizeStoredFeeConfiguration,
        TerminalDeviceConfigurationStorageV1Schema
    );

export const StoredTerminalDeviceConfigurationStorageV2Schema:
    z.ZodType<TerminalDeviceConfigurationStorageV2> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredVersionedFeeConfiguration(
                value,
                'terminal_device_configuration_version'
            ),
        TerminalDeviceConfigurationStorageV2Schema
    );

export const TerminalDeviceConfigurationVersionedObjectMarkerSchema:
    z.ZodType<{
        readonly terminal_device_configuration_version: unknown;
    }> =
    z.object({
        terminal_device_configuration_version: z.unknown(),
    }).passthrough();

export const StoredTerminalDeviceConfigurationSchema:
    z.ZodType<StoredTerminalDeviceConfiguration> =
    z.union([
        StoredTerminalDeviceConfigurationStorageV2Schema,
        StoredTerminalDeviceConfigurationStorageV1Schema,
    ]);

export function normalizeTerminalDeviceConfigurationStorageV1(
    config: TerminalDeviceConfiguration
): TerminalDeviceConfiguration {
    return config;
}

export function normalizeTerminalDeviceConfigurationStorageV2(
    config: TerminalDeviceConfigurationStorageV2
): TerminalDeviceConfiguration {
    return config.config;
}

export const TerminalDeviceConfigurationStorageContract =
    defineStoredJsonContract({
        contractName: 'TerminalDeviceConfiguration',
        semanticFieldValues: {},
        variants: {
            v1: {
                schemaName:
                    'StoredTerminalDeviceConfigurationStorageV1Schema',
                schema:
                    StoredTerminalDeviceConfigurationStorageV1Schema,
                guards: [
                    {
                        kind: 'json_marker_absent',
                        markerPath: [
                            'terminal_device_configuration_version'],
                    },
                ],
                normalize:
                    normalizeTerminalDeviceConfigurationStorageV1,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
            v2: {
                schemaName:
                    'StoredTerminalDeviceConfigurationStorageV2Schema',
                schema:
                    StoredTerminalDeviceConfigurationStorageV2Schema,
                guards: [
                    {
                        kind: 'json_marker_equals',
                        markerPath: [
                            'terminal_device_configuration_version'],
                        markerValue:
                            TerminalDeviceConfigurationStorageVersion.V2,
                    },
                ],
                normalize:
                    normalizeTerminalDeviceConfigurationStorageV2,
                semanticPaths: {},
                unrepresentedSemanticFields: [],
            },
        },
        writerVariant: 'v2',
    });

export const StoredTerminalDeviceConfigurationParser:
    z.ZodType<TerminalDeviceConfiguration> =
    buildStoredJsonValueParser(
        TerminalDeviceConfigurationStorageContract
    );

export function serializeTerminalDeviceConfigurationV2(
    config: TerminalDeviceConfiguration
): TerminalDeviceConfigurationStorageV2 {
    return {
        terminal_device_configuration_version:
            TerminalDeviceConfigurationStorageVersion.V2,
        config: TerminalDeviceConfigurationSchema.parse(config),
    };
}

export const ProcessorListItemSchema: z.ZodType<ProcessorListItem> = z.object({
    processor_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    status: z.enum(Status),
});

export const ProcessorSearchPageRequestSchema: z.ZodType<ProcessorSearchPageRequest> = z.object({
    q: z.string().trim().min(1).optional(),
    limit: PaymentsInfrastructureLimitSchema,
    cursor: z.string().trim().min(1).optional(),
});

export const ProcessorSearchRequestSchema: z.ZodType<ProcessorSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
        show_deleted: z.boolean().optional(),
    })
    .strict();
export const ProcessorNameParamSchema: z.ZodType<string> = NonEmptyStringSchema;

export const ProcessorSearchPageResponseSchema: z.ZodType<ProcessorSearchPageResponse> = z.object({
    processors: z.array(ProcessorListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const CreateProcessorRequestSchema: z.ZodType<CreateProcessorRequest> = z.object({
    processor_name: NonEmptyStringSchema,
    processor_config: ProcessorConfigSchema,
});

export const GetProcessorResponseSchema: z.ZodType<GetProcessorResponse> = z.object({
    processor_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    status: z.enum(Status),
    processor_config: ProcessorConfigSchema,
});

export const UpdateProcessorRequestSchema: z.ZodType<UpdateProcessorRequest> = z.object({
    processor_name: NonEmptyStringSchema.optional(),
    processor_config: ProcessorConfigSchema.optional(),
    expected_processor_config: ProcessorConfigSchema.optional(),
    status: z.enum(Status).optional(),
}).strict().refine(
    (value: UpdateProcessorRequest) =>
        value.processor_name !== undefined ||
        value.processor_config !== undefined ||
        value.status !== undefined,
    {
        message: 'At least one processor field must be provided.',
    }
).refine(
    (value: UpdateProcessorRequest) =>
        (value.processor_config === undefined) ===
        (value.expected_processor_config === undefined),
    {
        message: 'processor_config and expected_processor_config must be supplied together.',
        path: ['expected_processor_config'],
    }
);

const MerchantAccountSearchListItemSchemaBase = z.object({
    merchant_account_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    organization_uuid: z.string().uuid(),
    organization_name: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    applies_to_children: z.boolean(),
    status: z.enum(Status),
    allow_surcharge: z.boolean(),
    surcharge_pct: z.number().min(0).max(100),
    duplicate_processor_mid_count: z.number().int().min(1),
    has_duplicate_processor_mid: z.boolean(),
});
export const MerchantAccountSearchListItemSchema: z.ZodType<MerchantAccountSearchListItem> =
    MerchantAccountSearchListItemSchemaBase;

const MerchantAccountListItemSchemaBase = MerchantAccountSearchListItemSchemaBase.extend({
    merchant_account_id: PositiveIntSchema,
    organization_id: PositiveIntSchema,
});
export const MerchantAccountListItemSchema: z.ZodType<MerchantAccountListItem> =
    MerchantAccountListItemSchemaBase;

export const MerchantAccountSearchPageRequestSchema: z.ZodType<MerchantAccountSearchPageRequest> =
    z.object({
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        merchant_organization_uuid: z.string().uuid().optional(),
    }).strict();

export const MerchantAccountSearchRequestSchema: z.ZodType<MerchantAccountSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        show_deleted: z.boolean().optional(),
        merchant_organization_uuid: z.string().uuid().optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
        include_descendants: z.boolean().optional(),
    })
    .strict();
export const MerchantAccountIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const MerchantAccountUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const MerchantAccountSearchPageResponseSchema: z.ZodType<MerchantAccountSearchPageResponse> =
    z.object({
        merchant_accounts: z.array(MerchantAccountSearchListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
        duplicate_summary: z.object({
            has_duplicates: z.boolean(),
            duplicate_records_count: z.number().int().min(0),
        }),
    });

export const CreateMerchantAccountRequestSchema: z.ZodType<CreateMerchantAccountRequest> = z.object({
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    organization_uuid: z.string().uuid(),
    merchant_account_configuration: MerchantAccountConfigurationSchema,
}).strict();

export const UpdateMerchantAccountRequestSchema: z.ZodType<UpdateMerchantAccountRequest> = z.object({
    processor_name: NonEmptyStringSchema.optional(),
    processor_mid: NonEmptyStringSchema.optional(),
    applies_to_children: z.boolean().optional(),
    status: z.enum(Status).optional(),
    merchant_account_configuration: MerchantAccountConfigurationSchema.optional(),
    expected_merchant_account_configuration: MerchantAccountConfigurationSchema.optional(),
}).strict().refine(
    (value: UpdateMerchantAccountRequest) =>
        (value.merchant_account_configuration === undefined) ===
        (value.expected_merchant_account_configuration === undefined),
    {
        message:
            'merchant_account_configuration and expected_merchant_account_configuration must be supplied together.',
        path: ['expected_merchant_account_configuration'],
    }
);

export const GetMerchantAccountResponseSchema: z.ZodType<GetMerchantAccountResponse> =
    MerchantAccountSearchListItemSchemaBase.extend({
        merchant_account_configuration: MerchantAccountConfigurationSchema,
    });

export const LogicalTerminalListItemSchema = z.object({
    logical_terminal_uuid: z.string().uuid(),
    merchant_account_uuid: z.string().uuid(),
    name: z.string().nullable(),
    ipg_system_id: NumericIpgSystemIdSchema,
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    processor_tid: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    applies_to_children: z.boolean().nullable(),
    status: z.enum(Status),
    duplicate_processor_tid_count: z.number().int().min(1),
    has_duplicate_processor_tid: z.boolean(),
});

export const LogicalTerminalSearchPageRequestSchema: z.ZodType<LogicalTerminalSearchPageRequest> =
    z.object({
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        merchant_account_uuid: z.string().uuid().optional(),
    }).strict();

export const LogicalTerminalSearchRequestSchema: z.ZodType<LogicalTerminalSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        show_deleted: z.boolean().optional(),
        merchant_account_uuid: z.string().uuid().optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
    })
    .strict();
export const LogicalTerminalIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const LogicalTerminalUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const LogicalTerminalSearchPageResponseSchema: z.ZodType<LogicalTerminalSearchPageResponse> = z.object({
    logical_terminals: z.array(LogicalTerminalListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
    duplicate_summary: z.object({
        has_duplicates: z.boolean(),
        duplicate_records_count: z.number().int().min(0),
    }),
});

export const MerchantOrganizationMerchantAccountSummaryItemSchema = z.object({
    merchant_account_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    status: z.enum(Status),
    allow_surcharge: z.boolean(),
    surcharge_pct: z.number().min(0).max(100),
});

export const MerchantOrganizationListItemSchema = z.object({
    organization_uuid: z.string().uuid(),
    name: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    merchant_accounts: z.array(MerchantOrganizationMerchantAccountSummaryItemSchema),
});

export const MerchantOrganizationSearchPageRequestSchema: z.ZodType<MerchantOrganizationSearchPageRequest> =
    z.object({
        root_organization_uuid: z.string().uuid(),
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
    });

export const MerchantOrganizationSearchRequestSchema: z.ZodType<MerchantOrganizationSearchRequest> =
    z
        .object({
            root_organization_uuid: z.string().uuid(),
            terms: SearchTermsSchema,
            limit: PaymentsInfrastructureLimitSchema,
            cursor: z.string().trim().min(1).optional(),
        })
        .strict();

export const MerchantOrganizationSearchPageResponseSchema = z.object({
    merchant_organizations: z.array(MerchantOrganizationListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const PreviewLogicalTerminalOrganizationChangeRequestSchema: z.ZodType<PreviewLogicalTerminalOrganizationChangeRequest> =
    z.object({
        organization_uuid: z.string().uuid().nullable().optional(),
    }).strict();

export const LogicalTerminalOrganizationConflictItemSchema = z.object({
    terminal_device_uuid: z.string().uuid(),
    processor_device_id: NonEmptyStringSchema,
    organization_uuid: z.string().uuid(),
    organization_name: NonEmptyStringSchema,
});

export const PreviewLogicalTerminalOrganizationChangeResponseSchema = z.object({
    conflicts: z.array(LogicalTerminalOrganizationConflictItemSchema),
});

export const CreateLogicalTerminalRequestSchema: z.ZodType<CreateLogicalTerminalRequest> = z
    .object({
        merchant_account_uuid: z.string().uuid(),
        ipg_system_id: NumericIpgSystemIdSchema,
        name: z.string().trim().min(1).nullable().optional(),
        processor_tid: NonEmptyStringSchema,
        organization_uuid: z.string().uuid().nullable().optional(),
        logical_terminal_configuration: LogicalTerminalConfigurationSchema.optional(),
        create_default_physical_terminal_device: z.boolean().optional(),
        create_default_virtual_terminal_device: z.boolean().optional(),
    })
    .refine(
        (value: CreateLogicalTerminalRequest) =>
            !(
                value.create_default_physical_terminal_device === true &&
                value.create_default_virtual_terminal_device === true
            ),
        {
            message:
                'create_default_physical_terminal_device and ' +
                'create_default_virtual_terminal_device cannot both be true',
            path: ['create_default_virtual_terminal_device'],
        }
    )
    .strict();

export const UpdateLogicalTerminalRequestSchema: z.ZodType<UpdateLogicalTerminalRequest> = z.object({
    name: z.string().trim().min(1).nullable().optional(),
    processor_tid: NonEmptyStringSchema.optional(),
    organization_uuid: z.string().uuid().nullable().optional(),
    applies_to_children: z.boolean().nullable().optional(),
    clear_conflicting_terminal_device_organization_ids: z.boolean().optional(),
    status: z.enum(Status).optional(),
    logical_terminal_configuration: LogicalTerminalConfigurationSchema.optional(),
    expected_logical_terminal_configuration: LogicalTerminalConfigurationSchema.optional(),
    credential_uuid: z.string().uuid().nullable().optional(),
}).strict().refine(
    (value: UpdateLogicalTerminalRequest) =>
        (value.logical_terminal_configuration === undefined) ===
        (value.expected_logical_terminal_configuration === undefined),
    {
        message:
            'logical_terminal_configuration and expected_logical_terminal_configuration must be supplied together.',
        path: ['expected_logical_terminal_configuration'],
    }
);

export const GetLogicalTerminalResponseSchema: z.ZodType<GetLogicalTerminalResponse> =
    z.object({
        logical_terminal_uuid: z.string().uuid(),
        merchant_account_uuid: z.string().uuid(),
        name: z.string().nullable(),
        ipg_system_id: NumericIpgSystemIdSchema,
        processor_name: NonEmptyStringSchema,
        processor_mid: NonEmptyStringSchema,
        processor_tid: NonEmptyStringSchema,
        businessDetails: BusinessDetailsSchema.optional(),
        applies_to_children: z.boolean().nullable(),
        status: z.enum(Status),
        duplicate_processor_tid_count: z.number().int().min(1),
        has_duplicate_processor_tid: z.boolean(),
        organization_uuid: z.string().uuid().nullable(),
        logical_terminal_configuration: LogicalTerminalConfigurationSchema,
        credential_uuid: z.string().uuid().nullable(),
    });

export const TerminalDeviceListItemSchema = z.object({
    terminal_device_uuid: z.string().uuid(),
    logical_terminal_uuid: z.string().uuid(),
    name: z.string().nullable(),
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    processor_tid: NonEmptyStringSchema,
    processor_device_id: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    is_virtual: z.boolean(),
    status: z.enum(Status),
    duplicate_processor_device_id_count: z.number().int().min(1),
    has_duplicate_processor_device_id: z.boolean(),
});

export const TerminalDeviceSearchPageRequestSchema: z.ZodType<TerminalDeviceSearchPageRequest> =
    z.object({
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        logical_terminal_uuid: z.string().uuid().optional(),
    }).strict();

export const TerminalDeviceSearchRequestSchema: z.ZodType<TerminalDeviceSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        show_deleted: z.boolean().optional(),
        logical_terminal_uuid: z.string().uuid().optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
        include_descendants: z.boolean().optional(),
    })
    .strict();
export const TerminalDeviceIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const TerminalDeviceUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const TerminalDeviceSearchPageResponseSchema = z.object({
    terminal_devices: z.array(TerminalDeviceListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
    duplicate_summary: z.object({
        has_duplicates: z.boolean(),
        duplicate_records_count: z.number().int().min(0),
    }),
});

export const CreateTerminalDeviceRequestSchema: z.ZodType<CreateTerminalDeviceRequest> = z
    .object({
        logical_terminal_uuid: z.string().uuid(),
        processor_device_id: NonEmptyStringSchema,
        is_virtual: z.boolean(),
        organization_uuid: z.string().uuid().nullable().optional(),
        terminal_device_configuration: TerminalDeviceConfigurationSchema.optional(),
    })
    .strict();

export const UpdateTerminalDeviceRequestSchema: z.ZodType<UpdateTerminalDeviceRequest> = z.object({
    name: z.string().trim().min(1).nullable().optional(),
    processor_device_id: NonEmptyStringSchema.optional(),
    is_virtual: z.boolean().optional(),
    organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status).optional(),
    terminal_device_configuration: TerminalDeviceConfigurationSchema.optional(),
    expected_terminal_device_configuration: TerminalDeviceConfigurationSchema.optional(),
    credential_uuid: z.string().uuid().nullable().optional(),
}).strict().refine(
    (value: UpdateTerminalDeviceRequest) =>
        (value.terminal_device_configuration === undefined) ===
        (value.expected_terminal_device_configuration === undefined),
    {
        message:
            'terminal_device_configuration and expected_terminal_device_configuration must be supplied together.',
        path: ['expected_terminal_device_configuration'],
    }
);

export const GetTerminalDeviceResponseSchema: z.ZodType<GetTerminalDeviceResponse> =
    TerminalDeviceListItemSchema.extend({
        organization_uuid: z.string().uuid().nullable(),
        terminal_device_configuration: TerminalDeviceConfigurationSchema,
        credential_uuid: z.string().uuid().nullable(),
    });
